"use client"

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import clsx from "clsx"
import { Icon } from "@akinon/next/components"
import { twMerge } from 'tailwind-merge';

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={clsx("border-b border-[#CDCFCE]", className)}
    {...props}
  />
))
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & { icons?: string[], iconSize?: number, iconClassName?: string; headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'; removeHeading?: boolean; }
>(({ className, children, icons, iconSize = 16, iconClassName, headingLevel, removeHeading = false, ...props }, ref) => {
  if (removeHeading) {
    return (
      <AccordionPrimitive.Trigger
        ref={ref}
        className={clsx(
          "flex flex-1 items-center group justify-between py-4 transition-all font-medium",
          className
        )}
        {...props}
      >
        {children}
        {/* <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" /> */}
        { icons && (
          <div>
            <Icon name={icons[0]} size={iconSize} className={twMerge(clsx('group-data-[state=open]:hidden', iconClassName))} />
            <Icon name={icons[1]} size={iconSize} className={twMerge(clsx('group-data-[state=closed]:hidden', iconClassName))} />
          </div>
        )}
      </AccordionPrimitive.Trigger>
    );
  }

  const HeaderComponent = headingLevel || 'h3';

  return (
    <AccordionPrimitive.Header asChild className="flex">
      <HeaderComponent>
        <AccordionPrimitive.Trigger
          ref={ref}
          className={clsx(
            "flex flex-1 items-center group justify-between py-4 transition-all font-medium",
            className
          )}
          {...props}
        >
          {children}
          {/* <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" /> */}
          { icons && (
            <div>
              <Icon name={icons[0]} size={iconSize} className={twMerge(clsx('group-data-[state=open]:hidden', iconClassName))} />
              <Icon name={icons[1]} size={iconSize} className={twMerge(clsx('group-data-[state=closed]:hidden', iconClassName))} />
            </div>
          )}
        </AccordionPrimitive.Trigger>
      </HeaderComponent>
    </AccordionPrimitive.Header>
  );
})
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={clsx("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
))

AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
